import { PERMISSIONS, getPermissionResource } from '../main/teams/constants/permissions';

const READ_ACTION = /^(list|get|download|export)/;

// Named like reads but not reads: the server blocks them for impersonated
// sessions, so the UI should not offer them either.
const EXCLUDED_READ_PERMISSIONS = new Set([
  // GET with side effects (external call plus a timeline entry).
  PERMISSIONS.GetCryptoAmlPdfReport?.value || 'cryptoAml:getPdfReport',
]);

const ALL = PERMISSIONS.All.value;
const ONLY_TEST_MODE = PERMISSIONS.OnlyTestMode.value;

const isWildcard = (name) => name === ALL || name.endsWith(':*');

/** Every read-only permission value of the catalog, e.g. 'applicant:listApplicants'. */
export const READ_ONLY_PERMISSION_VALUES = Object.values(PERMISSIONS)
  .map(({ value }) => value)
  .filter((value) => {
    if (!value || isWildcard(value) || !value.includes(':')) {
      return false;
    }
    const action = value.slice(value.indexOf(':') + 1);

    return READ_ACTION.test(action) && !EXCLUDED_READ_PERMISSIONS.has(value);
  });

/**
 * Reduces role permissions to the read-only ones the role already grants.
 * Wildcards ('*', 'resource:*') are expanded into the catalog's read
 * permissions and never kept themselves; 'test' (sandbox-only restriction)
 * is kept as is. The result keeps the shape of the input entries
 * ({ name, accessLevel, ... }), so running it twice changes nothing.
 */
export const toReadOnlyPermissions = (permissions) => {
  if (!Array.isArray(permissions)) {
    return [];
  }

  const byName = new Map(permissions.filter((p) => p?.name).map((p) => [p.name, p]));
  const result = [];

  if (byName.has(ONLY_TEST_MODE)) {
    result.push(byName.get(ONLY_TEST_MODE));
  }

  READ_ONLY_PERMISSION_VALUES.forEach((value) => {
    const source = byName.get(value) || byName.get(`${getPermissionResource(value)}:*`) || byName.get(ALL);
    if (source) {
      result.push(source.name === value ? source : { ...source, name: value });
    }
  });

  return result;
};

/**
 * The role and permissions `setUserData` stores: unchanged for a normal
 * session, reduced to reads (on the role too) for a read-only admin session.
 */
export const resolveSessionRole = (role, impersonating) => {
  if (!impersonating) {
    return { role, permissions: role?.permissions };
  }

  const permissions = toReadOnlyPermissions(role?.permissions);

  return { role: role ? { ...role, permissions } : role, permissions };
};
